import { useState, useEffect } from 'react';
import PhoneInput from '@/components/feature/PhoneInput';

interface PatientFormData {
  nom: string;
  prenom: string;
  date_naissance: string;
  sexe: string;
  telephone: string;
  email: string;
  adresse: string;
  groupe_sanguin: string;
  allergies: string;
  antecedents_medicaux: string;
  maladies_chroniques: string;
  antecedents_chirurgicaux: string;
  medicaments_actuels: string;
  notes_medicales: string;
  personne_contact_nom: string;
  personne_contact_telephone: string;
}

interface PatientFormProps {
  initialData: PatientFormData;
  onChange: (data: PatientFormData) => void;
  readOnlyFields?: string[];
  isNewPatient?: boolean;
}

const GROUPES_SANGUINS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const SEXES = ['M', 'F'];

export default function PatientForm({ initialData, onChange, readOnlyFields = [], isNewPatient = false }: PatientFormProps) {
  const [form, setForm] = useState<PatientFormData>(initialData);

  useEffect(() => {
    setForm(initialData);
  }, [initialData.nom, initialData.prenom, initialData.date_naissance]);

  const updateField = (field: keyof PatientFormData, value: string) => {
    if (readOnlyFields.includes(field)) return;
    const updated = { ...form, [field]: value };
    setForm(updated);
    onChange(updated);
  };

  const isReadOnly = (field: string) => readOnlyFields.includes(field);

  return (
    <div className="space-y-8">
      {/* Informations personnelles */}
      <div>
        <h3 className="text-lg font-bold text-clinic-blue-dark mb-4 flex items-center gap-2">
          <i className="ri-user-line text-clinic-blue w-5 h-5 flex items-center justify-center"></i>
          Informations personnelles
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Nom *</label>
            <input
              type="text"
              value={form.nom}
              onChange={(e) => updateField('nom', e.target.value)}
              disabled={isReadOnly('nom')}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm disabled:bg-gray-100 disabled:text-gray-500"
              placeholder="Nom"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Prénom *</label>
            <input
              type="text"
              value={form.prenom}
              onChange={(e) => updateField('prenom', e.target.value)}
              disabled={isReadOnly('prenom')}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm disabled:bg-gray-100 disabled:text-gray-500"
              placeholder="Prénom"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Date de naissance *</label>
            <input
              type="date"
              value={form.date_naissance}
              onChange={(e) => updateField('date_naissance', e.target.value)}
              disabled={isReadOnly('date_naissance')}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm disabled:bg-gray-100 disabled:text-gray-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Sexe</label>
            <select
              value={form.sexe}
              onChange={(e) => updateField('sexe', e.target.value)}
              disabled={isReadOnly('sexe')}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm disabled:bg-gray-100 disabled:text-gray-500 appearance-none bg-white pr-8"
            >
              <option value="">Sélectionner...</option>
              {SEXES.map((s) => (
                <option key={s} value={s}>{s === 'M' ? 'Masculin' : 'Féminin'}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Téléphone</label>
            <PhoneInput
              value={form.telephone}
              onChange={(val) => updateField('telephone', val)}
              disabled={isReadOnly('telephone')}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
              placeholder="email@exemple.com"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Adresse</label>
            <input
              type="text"
              value={form.adresse}
              onChange={(e) => updateField('adresse', e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
              placeholder="Adresse complète"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Groupe sanguin</label>
            <select
              value={form.groupe_sanguin}
              onChange={(e) => updateField('groupe_sanguin', e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm appearance-none bg-white pr-8"
            >
              <option value="">Inconnu</option>
              {GROUPES_SANGUINS.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Contact d'urgence */}
      <div>
        <h3 className="text-lg font-bold text-clinic-blue-dark mb-4 flex items-center gap-2">
          <i className="ri-phone-line text-clinic-blue w-5 h-5 flex items-center justify-center"></i>
          Contact d'urgence
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Nom de la personne</label>
            <input
              type="text"
              value={form.personne_contact_nom}
              onChange={(e) => updateField('personne_contact_nom', e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
              placeholder="Nom du contact d'urgence"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Téléphone du contact</label>
            <input
              type="tel"
              value={form.personne_contact_telephone}
              onChange={(e) => updateField('personne_contact_telephone', e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
              placeholder="+33 6 12 34 56 78"
            />
          </div>
        </div>
      </div>

      {/* Informations médicales */}
      <div>
        <h3 className="text-lg font-bold text-clinic-blue-dark mb-4 flex items-center gap-2">
          <i className="ri-heart-pulse-line text-clinic-blue w-5 h-5 flex items-center justify-center"></i>
          Informations médicales
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Allergies</label>
            <textarea
              value={form.allergies}
              onChange={(e) => updateField('allergies', e.target.value)}
              rows={2}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
              placeholder="Allergies connues..."
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Maladies chroniques</label>
            <textarea
              value={form.maladies_chroniques}
              onChange={(e) => updateField('maladies_chroniques', e.target.value)}
              rows={2}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
              placeholder="Diabète, HTA, asthme..."
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Antécédents médicaux</label>
            <textarea
              value={form.antecedents_medicaux}
              onChange={(e) => updateField('antecedents_medicaux', e.target.value)}
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
              placeholder="Antécédents médicaux significatifs..."
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Antécédents chirurgicaux</label>
            <textarea
              value={form.antecedents_chirurgicaux}
              onChange={(e) => updateField('antecedents_chirurgicaux', e.target.value)}
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
              placeholder="Chirurgies antérieures..."
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Médicaments actuels</label>
            <textarea
              value={form.medicaments_actuels}
              onChange={(e) => updateField('medicaments_actuels', e.target.value)}
              rows={2}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
              placeholder="Traitement en cours..."
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes médicales</label>
            <textarea
              value={form.notes_medicales}
              onChange={(e) => updateField('notes_medicales', e.target.value)}
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
              placeholder="Notes supplémentaires..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export type { PatientFormData };